import React, { useEffect, useRef, useState } from "react";
import styles from "../../../Handbook.module.css";

interface ActionsMenuProps {
    onEdit?: () => void;
    onDelete?: () => void;
}

interface Position {
    top: number;
    left: number;
}

export const ActionsMenu = ({ onEdit, onDelete }: ActionsMenuProps) => {
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<Position | null>(null);

    const calcPosition = () => {
        if (!btnRef.current) return;

        const rect = btnRef.current.getBoundingClientRect();
        const menuW = 180;
        const menuH = 96;

        const openUp = window.innerHeight - rect.bottom < menuH + 12;
        const top = openUp ? rect.top - menuH - 8 : rect.bottom + 8;

        const leftPreferred = rect.right - menuW;
        const left = Math.min(
            Math.max(8, leftPreferred),
            window.innerWidth - menuW - 8
        );

        const topClamped = Math.min(
            Math.max(8, top),
            window.innerHeight - menuH - 8
        );

        setPos({ top: topClamped, left });
    };

    useEffect(() => {
        if (!open) return;

        calcPosition();

        const onResize = () => calcPosition();
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        const onScroll = () => setOpen(false);
        const onClickOutside = (e: MouseEvent) => {
            const t = e.target as Node;
            if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) {
                setOpen(false);
            }
        };

        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onScroll, true);
        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("mousedown", onClickOutside);

        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onScroll, true);
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("mousedown", onClickOutside);
        };
    }, [open]);

    const handleEdit = () => {
        setOpen(false);
        onEdit?.();
    };

    const handleDelete = () => {
        setOpen(false);
        onDelete?.();
    };

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                className={styles.actionsBtn}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                …
            </button>

            {open && pos && (
                <div
                    ref={menuRef}
                    className={styles.actionsMenu}
                    style={{ top: pos.top, left: pos.left }}
                    role="menu"
                >
                    <button
                        type="button"
                        className={styles.actionsMenuItem}
                        role="menuitem"
                        onClick={handleEdit}
                        disabled={!onEdit}
                        aria-disabled={!onEdit}
                    >
                        Редактировать
                    </button>

                    <div className={styles.actionsMenuDivider} />
                    {
                        onDelete && (
                            <button
                                type="button"
                                className={`${styles.actionsMenuItem} ${styles.actionsMenuItemDanger}`}
                                role="menuitem"
                                onClick={handleDelete}
                                disabled={!onDelete}
                                aria-disabled={!onDelete}
                            >
                                Удалить
                            </button>
                        )
                    }

                </div>
            )}
        </>
    );
};
